// Integração com o simulador de IR (chat + simulador) auto-hospedado em
// public/vendor/auc-simulador-widget.js, usado na landing de campanha e no checkout.

import { incentiveLawLabelFromNames } from "@/scripts/helpers/incentiveLawLabel.helper";

const SCRIPT_ID = "auc-simulador-script";
const HOST_ID = "auc-sim-host";
const STORAGE_KEY = "simulationResult";

export const SIMULATOR_RESULT_EVENT = "auc-simulador:resultado";
export const SIMULATOR_DONATE_EVENT = "auc-simulador:doar";
export const SIMULATOR_SOURCE = "auc-simulador-widget";

export function themeColorToHex(themeValue) {
  try {
    const arr = typeof themeValue === "string" ? JSON.parse(themeValue) : themeValue;
    if (!Array.isArray(arr) || arr.length < 3) return null;
    const toHex = (n) => {
      const h = Math.max(0, Math.min(255, Math.round(Number(n)))).toString(16);
      return h.length === 1 ? "0" + h : h;
    };
    return "#" + toHex(arr[0]) + toHex(arr[1]) + toHex(arr[2]);
  } catch (e) {
    return null;
  }
}

/**
 * Injeta o widget. mode "inline" exige o seletor do container em target;
 * mode "none" monta como modal, sem botão flutuante, aberto por openSimulatorWidget().
 * O widget só existe uma vez por página: chamar de novo sem desmontar não faz nada.
 */
export function mountSimulatorWidget({ mode, target, campaignSlug, simulatorPercentage, themePrimaria, themeSecundaria, donateLabel, typeLabel }) {
  if (typeof document === "undefined" || document.getElementById(SCRIPT_ID)) return;

  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.src = "/vendor/auc-simulador-widget.js";
  s.setAttribute("data-mode", mode);
  if (target) s.setAttribute("data-target", target);
  if (campaignSlug) s.setAttribute("data-campaign", campaignSlug);
  // tipo fixado aqui: sem ele o widget consulta a API de homologação para descobrir 6%/7%
  s.setAttribute("data-campaign-type", Number(simulatorPercentage) === 7 ? "esporte" : "cultura");
  s.setAttribute("data-color", themeColorToHex(themePrimaria) || "#009CA6");
  s.setAttribute("data-color-secondary", themeColorToHex(themeSecundaria) || "#8CC63F");
  // nome da causa no botão "Tipo de incentivo" (AUC-125). Sem ele o widget cai no rótulo
  // por percentual, que chama de "Cultura/Social" toda campanha que não é de esporte.
  if (typeLabel) s.setAttribute("data-type-label", typeLabel);
  // botão de doar abaixo de "Calcular meu potencial"; o clique dispara SIMULATOR_DONATE_EVENT
  if (donateLabel) s.setAttribute("data-donate-label", donateLabel);
  document.body.appendChild(s);
}

/**
 * Nome da causa do botão "Tipo de incentivo", a partir das leis de incentivo dos
 * projetos da campanha (AUC-125).
 *
 * A fonte é Campaign/FindBySubdomainAndOrProjectId, e não a projectList da landing:
 * aquela lista é FILTRADA pelos seletores de causa/cidade e limitada por howMany, então
 * o rótulo mudaria conforme o visitante filtrasse. O endpoint devolve as leis da
 * campanha inteira.
 *
 * Devolve "" quando não dá para afirmar nada -- falha de rede, campanha sem projeto,
 * lei fora das quatro cadastradas. O widget então mantém o rótulo por percentual.
 */
export function resolveCampaignTypeLabel(campaignService, campaignSlug) {
  if (!campaignService || !campaignSlug) return Promise.resolve("");

  return campaignService
    .getCampaignMetaSilently(campaignSlug)
    .then((meta) => incentiveLawLabelFromNames(meta && meta.incentiveLaws) || "")
    .catch(() => "");
}

export function openSimulatorWidget() {
  if (window.AUCSimulador && typeof window.AUCSimulador.open === "function") {
    window.AUCSimulador.open();
    return true;
  }
  return false;
}

// Remove o widget para que a próxima tela o monte limpo (outro modo ou outro container).
export function unmountSimulatorWidget() {
  const script = document.getElementById(SCRIPT_ID);
  if (script && script.parentNode) script.parentNode.removeChild(script);
  const host = document.getElementById(HOST_ID);
  if (host && host.parentNode) host.parentNode.removeChild(host);
  window.__aucSimLoaded = undefined;
  window.AUCSimulador = undefined;
}

/**
 * Grava o resultado do evento do widget no mesmo formato do Simulation.vue, lido pelo
 * flutuante e pelo checkout. Devolve o potencial gravado, ou 0 quando não há potencial
 * (como no Simulation.vue, a simulação anterior é mantida nesse caso).
 */
export function saveSimulatorResult(detail) {
  const sim = (detail && detail.resultado) || {};
  const inp = (detail && detail.entradas) || {};
  if (sim.estado !== "completa_vantajosa" || !(sim.potencial > 0)) return 0;

  const result = Math.round(sim.potencial * 100) / 100;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      monthlySalary: inp.salary,
      monthlyProceeds: inp.rental,
      annualBonus: inp.bonus,
      childrenQuantity: inp.dependents,
      monthlyEducationExpense: inp.education,
      monthlyMedicalExpense: inp.health,
      otherDeductions: inp.other,
      privatePension: inp.pgbl,
      result,
      source: SIMULATOR_SOURCE
    }));
  } catch (e) {
    return 0;
  }
  return result;
}

export function isSimulatorResultFromWidget() {
  try {
    const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return !!data && data.source === SIMULATOR_SOURCE;
  } catch (e) {
    return false;
  }
}
